import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

export interface RefreshButtonProps {
	/**
	 * The refetch function to call when the button is clicked.
	 * Can be from Apollo's useQuery or any async function.
	 */
	onRefresh: (() => void | Promise<any>) | undefined;
	
	/**
	 * External loading state (optional).
	 * If provided, the button will be disabled when this is true.
	 */
	isLoading?: boolean;
	
	/**
	 * Button variant (optional, defaults to 'outline').
	 */
	variant?: 'outline' | 'default' | 'ghost' | 'link' | 'destructive' | 'secondary';
	
	/**
	 * Button size (optional, defaults to 'sm').
	 */
	size?: 'default' | 'sm' | 'lg' | 'icon';
	
	/**
	 * Custom class name (optional).
	 */
	className?: string;
	
	/**
	 * Show label text (optional, defaults to true).
	 */
	showLabel?: boolean;
}

/**
 * A generic refresh button component that:
 * - Shows loading animation during refresh
 * - Handles errors gracefully and allows retry
 * - Can be disabled during external loading states
 * - Works with both synchronous and asynchronous refetch functions
 */
export const RefreshButton = ({
	onRefresh,
	isLoading: externalLoading = false,
	variant = 'outline',
	size = 'sm',
	className,
	showLabel = true
}: RefreshButtonProps) => {
	const { t } = useTranslation('common');
	const [internalLoading, setInternalLoading] = useState(false);
	
	const isLoading = externalLoading || internalLoading;
	
	const handleRefresh = async () => {
		if (!onRefresh || isLoading) {
			return;
		}
		
		try {
			setInternalLoading(true);
			const result = onRefresh();
			
			// If the function returns a promise, wait for it to complete
			if (result && typeof result === 'object' && 'then' in result) {
				await result;
			}
		} catch (error) {
			console.error('Error refreshing data:', error);
			// Don't prevent retry - user can click again
		} finally {
			setInternalLoading(false);
		}
	};
	
	return (
		<Button
			variant={variant}
			size={size}
			onClick={handleRefresh}
			disabled={isLoading}
			className={className}
		>
			<RefreshCw
				className={`h-4 w-4 ${showLabel ? 'mr-2' : ''} ${isLoading ? 'animate-spin' : ''}`}
			/>
			{showLabel && t('buttons.refresh')}
		</Button>
	);
};
